
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { AsyncValidatorFn, FormControl, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { SwiperAutoplayInterface, SwiperComponent, SwiperConfigInterface } from "ngx-swiper-wrapper";
import { Observable } from "rxjs/Observable";
import { Subscription } from "rxjs/Subscription";
import { LoginDialogComponent } from "../../modalDialogs";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { CoreCOMService } from "../../services/com.service";
import { IOSHelperService } from "../../services/IOSHelper.service";
import { CoreLanguageService } from "../../services/language.service";
import { CoreLogService } from "../../services/log.service";
import { RouterHelperService } from "../../services/routerHelper.service";
import { UserHelperService } from "../../services/userHelper.service";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { IAppCoupons, RedemptionResult } from "./couponRedeem.facade";
import { CouponRedeemService } from "./couponRedeem.service";
import { ValidatorsConfig, dynamicMinLengthValidator, dynamicAsyncMaxLengthValidator } from "../../shared/validator/validator.function";
import { CouponRedemptionService } from "../../services/couponRedemption.service";

@Component({
  selector: "couponRredeem",
  templateUrl: "./couponRedeem.component.html",
  styleUrls: ["./couponRedeem.component.scss"],
  providers: [CouponRedeemService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class CouponRedeemComponent implements OnInit, OnDestroy {
  private _inputSubscriber: Subscription;
  private _inputValidationSubscriber: Subscription;

  public appCoupons$: Observable<ReadonlyArray<IAppCoupons>>
  public useScan: boolean = false;
  private _requiredCouponLength: number;
  public placeholder: string = "";
  public isAppCouponActive: boolean;
  public isLogin: boolean = false;
  public isMobile: boolean = false;

  public isSmallView: boolean = false;

  public sliderMoved: boolean = false;

  public flashEnabled: boolean = false;
  public hasAccess: boolean = true;
  // browser scanner
  public zXingScanner: boolean;
  public hasDevices: boolean;
  public hasPermission: boolean;
  /** Guards openAppCouponProduct against a second concurrent tap firing a duplicate reservation. */
  private _isRedeeming: boolean = false;

  @ViewChild('swiperCoupons') swiperCoupons: SwiperComponent;

  constructor(private _couponRedeemService: CouponRedeemService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _coreCOMService: CoreCOMService
    , private _mainnavService: MainnavService
    , private _userHelperService: UserHelperService
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _iosHelperService: IOSHelperService
    , private _routerHelperService: RouterHelperService
    , private _router: Router
    , private _couponRedemptionService: CouponRedemptionService
  ) {
    const me = this;
    me.isSmallView = me._mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("CouponRedeemComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /** Synchronous snapshot — delegates to the single-source-of-truth service instead of reading the cart stream directly. */
  public isAppCouponInCart(couponId: number): boolean {
    const me = this;
    return me._couponRedemptionService.isInCart(couponId);
  }

  ngOnInit() {
    const me = this;
    me.isMobile = me._coreCOMService.environment.client.isMobile;
    me.isAppCouponActive = me._couponRedeemService.isAppCouponActive;
    if (me.isMobile && me.isAppCouponActive) {
      me.isLogin = me._userHelperService.IsRegistered;
      me._couponRedeemService.loadAppCoupons();
      me.appCoupons$ = me._couponRedeemService.appCoupons$
    }
    me.checkUseScanCoupon();
    me.initValidatorsAndPlaceholder();

    me._inputValidationSubscriber = me.inputFormControl.statusChanges.subscribe(() => {
      me.PushChanges();
    });

    me._inputSubscriber = me.inputFormControl.valueChanges.subscribe(() => {
      me.inputFormControl.markAsTouched();
      me.PushChanges();
    });
    me.PushChanges();
  }

  ngOnDestroy() {
    const me = this;
    if (me._inputSubscriber) {
      me._inputSubscriber.unsubscribe()
    }
    if (me._inputValidationSubscriber) {
      me._inputValidationSubscriber.unsubscribe()
    }
  }

  private async checkUseScanCoupon() {
    const me = this;
    const useScan = await me._couponRedeemService.getUseScanCoupon();
    if (me.useScan != useScan) {
      me.useScan = useScan;
      me.PushChanges();
    }
  }

  /** Configures validators + placeholder at component init; validators are fixed at entry because the component is recreated on each route activation. */
  private async initValidatorsAndPlaceholder(): Promise<void> {
    const me = this;
    const requiredLength = await me._couponRedeemService.getRequiredLength();
    me._requiredCouponLength = requiredLength;

    const syncValidators = [Validators.pattern(ValidatorsConfig.PATTERN_NUMBERS_ONLY)];
    const asyncValidators: AsyncValidatorFn[] = [
      dynamicMinLengthValidator(() => me._couponRedeemService.getRequiredLength()),
      dynamicAsyncMaxLengthValidator(() => me._couponRedeemService.getRequiredLength()),
    ];

    me.inputFormControl.setValidators(syncValidators);
    me.inputFormControl.setAsyncValidators(asyncValidators);
    me.inputFormControl.updateValueAndValidity();

    if (!isNaN(requiredLength)) {
      me.placeholder = me._coreLanguageService.value("phCoupon_Redeem_Input_Placeholder").replace("{requiredLength}", me._requiredCouponLength.toString());
    }
    me.PushChanges();
  }

  public get CouponRedeemErrorMinLength(): string {
    const me = this;
    const lengthStr = me._requiredCouponLength != null ? me._requiredCouponLength.toString() : "";
    return me._coreLanguageService.value("phCoupon_Redeem_Error_MinLength").replace("{requiredLength}", lengthStr);
  }

  public get CouponRedeemErrorMaxLength(): string {
    const me = this;
    const lengthStr = me._requiredCouponLength != null ? me._requiredCouponLength.toString() : "";
    return me._coreLanguageService.value("phCoupon_Redeem_Error_MaxLength").replace("{requiredLength}", lengthStr);
  }

  public inputFormControl = new FormControl('', {
    validators: [
      Validators.pattern(ValidatorsConfig.PATTERN_NUMBERS_ONLY),
    ],
  });

  private appCouponSliderAutoPlay: SwiperAutoplayInterface = {
    delay: 3000,
    disableOnInteraction: false
  };

  public appCouponSliderconfig(appCouponLength: number) {
    const me = this;
    const sliderconfig: SwiperConfigInterface = {
      init: true,
      direction: "horizontal",
      slidesPerView: 3,
      keyboard: false,
      mousewheel: false,
      scrollbar: false,
      navigation: false,
      spaceBetween: 10,
      autoplay: appCouponLength > 3 ? me.appCouponSliderAutoPlay : false
    }
    return sliderconfig;
  }

  public resizeSwiper(duration = 1500, step = 300) {
    const me = this;
    if (duration > 0) {
      if (me.swiperCoupons) {
        if (me.swiperCoupons.directiveRef) {
          if (me.swiperCoupons.directiveRef.swiper()) {
            me.swiperCoupons.directiveRef.update();
          }
        }
      }
      setTimeout(() => {
        me.resizeSwiper(duration - step, step);
      }, step)
    }
  }

  public loadappCoupons() {
    const me = this;
    me._router.navigate(["/coupon"]);
  }

  /** Thin delegator for the code-entry form — resolves raw code through the funnel. */
  public async OpenCouponProductByEnteringCode(): Promise<void> {
    const me = this;
    const couponCodeInputValue: string = me.inputFormControl.value;
    const result = await me._couponRedemptionService.resolveCouponCodeRedemption(couponCodeInputValue.trim());
    me.handleRedemptionResult(result);
  }

  /** Thin delegator for slider-item tap — resolves coupon object through the funnel; guarded against a double-tap re-entrancy. */
  public async openAppCouponProduct(appCoupon: IAppCoupons): Promise<void> {
    const me = this;
    if (me._isRedeeming) {
      return;
    }
    me._isRedeeming = true;
    try {
      const result = await me._couponRedemptionService.resolveAppCouponRedemption(appCoupon);
      me.handleRedemptionResult(result);
    } finally {
      me._isRedeeming = false;
    }
  }

  /** Routes component-specific RedemptionResult cases; delegates common cases to the service router. */
  private handleRedemptionResult(result: RedemptionResult): void {
    const me = this;
    switch (result) {
      case RedemptionResult.LoginRequired:
        me.openLoginDialog();
        break;
      case RedemptionResult.UnsupportedOrderMode:
        me._couponRedemptionService.openUnsupportedOrderModeDialog();
        break;
      case RedemptionResult.Proceed:
        me.PushChanges();
        break;
      case RedemptionResult.AlreadyInCart:
        // Deliberately local: AlreadyInCart must not clearInputValue — the user may still want to keep the code visible.
        me._couponRedemptionService.openInCartDialog(() => me.PushChanges());
        break;
      default:
        me._couponRedemptionService.routeRedemptionResult(result, () => {
          me.clearInputValue();
          me.PushChanges();
        });
        break;
    }
  }

  public openLoginDialog(): void {
    const me = this;
    me._bottomSheetHelperService.open(LoginDialogComponent);
  }

  public clearInputValue() {
    const me = this;
    if (me.inputFormControl.value !== "") {
      me.inputFormControl.setValue("")
      me.inputFormControl.markAsUntouched();
    }
  }

  public setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  public NavigateToScanCoupon() {
    const me = this;
    me._router.navigate(["/couponCodeScanner"]);
  }

  public handleNavigateBack() {
    const me = this;
    me.navigateBack();
  }

  private navigateBack(): void {
    const me = this;
    me._routerHelperService.navigateBackSimple();
  }

  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true && !me.sliderMoved) {
      me.handleNavigateBack();
    }
  }

}